import { Button, Menu, MenuItem, Navbar, Popover, Position } from '@blueprintjs/core';
import { Duplicate, Insert, More, Plus, Trash } from '@blueprintjs/icons';
import { isEqual } from 'lodash';
import { reaction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { onSnapshot } from 'mobx-state-tree';
import { Audios as PolotnoAudios } from 'polotno/pages-timeline/audios';
import { CurrentTime as PolotnoCurrentTime } from 'polotno/pages-timeline/current-time';
import { ElementTrack, ELEMENT_ROW_HEIGHT } from 'polotno/pages-timeline/element-track';
import { Pages as PolotnoPages } from 'polotno/pages-timeline/pages';
import { PlayButton as PolotnoPlayButton } from 'polotno/pages-timeline/play-button';
import { TimeRuler as PolotnoTimeRuler } from 'polotno/pages-timeline/time-ruler';
import { forEveryChild } from 'polotno/model/group-model';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ReactSortable } from 'react-sortablejs';

import { storeHasVideoElements, storeHasAudioElements, syncPageVideoDuration } from '../../utilities';
import { isSafeZoneElement } from '../../utilities/safeZone';
import {
	MIN_TRACK_DURATION_MS,
	TIMELINE_DEFAULT_HEIGHT,
	TIMELINE_MAX_HEIGHT,
	TIMELINE_MIN_HEIGHT,
} from '../constants';
import './PagesTimeline.scss';

interface PageThumbnailProps {
	page: any;
	maxPages?: number;
	t?: (key: string, fallback?: string) => string;
	isListingOverlay?: boolean;
}

interface SortablePagesProps {
	store: any;
	maxPages?: number;
	t?: (key: string, fallback?: string) => string;
	isListingOverlay?: boolean;
}

interface PagesTimelineProps {
	store: any;
	t?: (key: string, fallback?: string) => string;
	globalState?: any;
	isVideoEditingEnabled?: boolean;
}

interface ThumbnailQueueItem {
	page: any;
	setPreview: (preview: string) => void;
}

interface PageListItem {
	id: string;
}

// Queue for generating page thumbnails
let thumbnailQueue: ThumbnailQueueItem[] = [];
let isProcessing = false;

// Keep values inside timeline-safe bounds to avoid negative/overflow times.
const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
const TIMELINE_RESIZE_STEP = 16;

const getTimelineTracks = (store) => {
	const tracks = [];
	const rowEndTimes = [];

	// Rebuild the same timeline model used by Polotno: every visible element
	// becomes a time segment (start/end) and is packed into the first row
	// where it doesn't overlap with previous segments.
	const timelineItems = store.pages
		.slice()
		.flatMap((page) => {
			const pageElements = [];

			// Groups render as child tracks in timeline, so flatten nested items.
			for (const child of page.children) {
				if (isSafeZoneElement(child)) {			// Skip safe-zone overlay bands
					continue;
				}
				if (child.type === 'group') {
					forEveryChild(child, (nested) => {
						if (nested.type !== 'group') {
							pageElements.push(nested);
						}
					});
				} else {
					pageElements.push(child);
				}
			}

			return pageElements.map((element) => {
				const animations = Array.isArray(element?.animations) ? element.animations : [];
				const enterAnimation = animations.find((animation) => animation.type === 'enter');
				const exitAnimation = animations.find((animation) => animation.type === 'exit');

				// Convert local page timing (enter/exit delays) into absolute
				// timeline timing by adding page.startTime.
				const localStart = clamp(enterAnimation?.delay ?? 0, 0, page?.duration);
				const localEnd = Math.max(
					localStart + MIN_TRACK_DURATION_MS,
					clamp(page.duration - (exitAnimation?.delay ?? 0), localStart + MIN_TRACK_DURATION_MS, page?.duration),
				);

				return {
					element,
					page,
					start: page.startTime + localStart,
					end: page.startTime + localEnd,
				};
			});
		})
		.sort((a, b) => a.start - b.start);

	// Greedy row packing: place each item in the first row that has ended.
	for (const item of timelineItems) {
		let row = 0;
		while (row < rowEndTimes.length && rowEndTimes[row] > item.start) {
			row += 1;
		}

		rowEndTimes[row] = item.end;
		tracks.push({ ...item, row });
	}

	return tracks;
};

//Filter out elements that are not visible in the timeline (e.g., safe-zone overlays)
const FilteredElementsTimeline = observer(({ store, scale, width }: { store: any; scale: number; width: number }) => {
	const tracks = getTimelineTracks(store);
	if (!tracks.length) {
		return null;
	}

	const height = (tracks.reduce((max, t) => Math.max(max, t.row), -1) + 1) * ELEMENT_ROW_HEIGHT;

	return (
		<div
			className="polotno-elements-container"
			style={{ position: 'relative', minWidth: `${width}px`, height: `${height}px`, marginTop: '12px' }}
		>
			{tracks.map(({ element, page, row }) => (
				<ElementTrack
					key={`${page.id}-${element.id}`}
					element={element}
					store={store}
					page={page}
					scale={scale}
					row={row}
				/>
			))}
		</div>
	);
});

const processThumbnailQueue = async () => {
	if (isProcessing || thumbnailQueue.length === 0) {
		return;
	}
	isProcessing = true;
	const { page, setPreview } = thumbnailQueue.shift()!;
	try {
		const preview = await page.store.toDataURL({
			pageId: page.id,
			pixelRatio: 0.1,
			quickMode: true,
		});
		setPreview(preview);
	} catch (error: any) {
		if (!error.message.includes('Canvas was unmounted.')) {
			console.error('[PagesTimeline] Thumbnail generation error:', error);
		}
	}
	isProcessing = false;
	processThumbnailQueue();
};

/**
 * Page thumbnail component with drag/drop support
 */
const PageThumbnail: React.FC<PageThumbnailProps> = observer(({ page, maxPages, t, isListingOverlay = false }) => {
	const [preview, setPreview] = useState<string | null>(null);
	const isSelected =
		page.store.activePage === page ||
		(page.store._selectedPagesIds && page.store._selectedPagesIds.includes(page.id));
	const store = page.store;
	const isVisible = useRef(false);
	const pageIndex = store.pages.indexOf(page);
	const containerRef = useRef<HTMLDivElement>(null);
	const thumbnailWidth = (60 / page.computedHeight) * page.computedWidth;
	const containerWidth = thumbnailWidth;
	const pageCount = store.pages.length;
	const canDeletePage = pageCount > 1;
	const hasReachedLimit = maxPages && pageCount >= maxPages;

	// Set container width
	useLayoutEffect(() => {
		if (containerRef.current) {
			containerRef.current.style.width = `${containerWidth}px`;
		}
	}, [pageIndex, containerWidth]);

	// Handle thumbnail generation
	useEffect(() => {
		const queueThumbnail = () => {
			thumbnailQueue.push({ page, setPreview });
			processThumbnailQueue();
		};

		let debounceTimer: ReturnType<typeof setTimeout> | null = null;
		let inactivityTimer: ReturnType<typeof setTimeout> | null = null;
		let lastUpdate = Date.now();

		const debouncedUpdate = () => {
			if (debounceTimer) clearTimeout(debounceTimer);

			if (isVisible.current) {
				// Update inactivity timer
				if (!inactivityTimer) {
					inactivityTimer = setTimeout(() => {
						if (Date.now() - lastUpdate >= 6000) {
							queueThumbnail();
							lastUpdate = Date.now();
							inactivityTimer = null;
						}
					}, 6000);
				}

				debounceTimer = setTimeout(() => {
					queueThumbnail();
					lastUpdate = Date.now();
					debounceTimer = null;
					if (inactivityTimer) {
						clearTimeout(inactivityTimer);
						inactivityTimer = null;
					}
				}, 300);
			}
		};

		let previousSnapshot: any = null;
		const snapshotDisposer = onSnapshot(page, (snapshot) => {
			if (!isEqual(previousSnapshot, snapshot)) {
				debouncedUpdate();
				previousSnapshot = snapshot;
			}
		});

		const editModeReaction = reaction(
			() => page.children.some((child: any) => child._editModeEnabled),
			(isEditing) => {
				if (!isEditing) {
					debouncedUpdate();
				}
			},
		);

		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						isVisible.current = true;
						debouncedUpdate();
					} else {
						if (debounceTimer) clearTimeout(debounceTimer);
						if (inactivityTimer) clearTimeout(inactivityTimer);
						isVisible.current = false;
					}
				});
			},
			{ threshold: 0.1 },
		);

		if (containerRef.current) {
			observer.observe(containerRef.current);
		}

		return () => {
			observer.disconnect();
			if (debounceTimer) clearTimeout(debounceTimer);
			if (inactivityTimer) clearTimeout(inactivityTimer);
			snapshotDisposer();
			editModeReaction();
			thumbnailQueue = thumbnailQueue.filter((item) => item.page !== page);
		};
	}, [page]);

	const handleClick = (e: React.MouseEvent) => {
		if (isListingOverlay) {
			store.selectPages([page.id]);
			return;
		}
		const selectedIds = (store._selectedPagesIds && store._selectedPagesIds.length)
			? store._selectedPagesIds
			: [store.activePage?.id];
		const isCurrentlySelected = selectedIds.includes(page.id);
		const isShiftKey = e && e.shiftKey;

		if (isShiftKey && isCurrentlySelected) {
			// Remove from selection
			const newSelection = selectedIds.filter((id: string) => id !== page.id);
			store.selectPages(newSelection);
		} else if (isShiftKey && !isCurrentlySelected) {
			// Add to selection
			const newSelection = selectedIds.concat([page.id]);
			store.selectPages(newSelection);
		} else {
			// Select only this page
			store.selectPages([page.id]);
		}
	};

	const handleDuplicate = () => {
		if (hasReachedLimit) return;

		const selectedPages = (store.selectedPages || []).filter(Boolean);
		const pagesToDuplicate = selectedPages.length ? selectedPages : store.activePage ? [store.activePage] : [];
		if (!pagesToDuplicate.length) return;

		const selectedIds = new Set(pagesToDuplicate.map((p: any) => p.id));
		const orderedPages = store.pages.filter((p: any) => selectedIds.has(p.id));
		const lastPage = orderedPages[orderedPages.length - 1];
		const clonedPages = orderedPages.map((p: any) => p.clone());
		const insertIndex = store.pages.indexOf(lastPage);

		clonedPages.forEach((clonedPage: any, i: number) => {
			clonedPage.setZIndex(insertIndex + 1 + i);
		});

		store.selectPages(clonedPages.map((p: any) => p.id));
	};

	const handleInsert = () => {
		if (hasReachedLimit) return;

		const newPage = store.addPage({
			bleed: store.activePage?.bleed || 0,
			width: store.activePage?.width || 'auto',
			height: store.activePage?.height || 'auto',
		});
		const insertIndex = store.pages.indexOf(page);
		newPage.setZIndex(insertIndex + 1);
	};

	const handleDelete = () => {
		const selectedPages = (store.selectedPages || []).filter(Boolean);
		const pagesToDelete = selectedPages.length
			? selectedPages.map((p: any) => p.id)
			: store.activePage
				? [store.activePage.id]
				: [];
		if (pagesToDelete.length) {
			store.deletePages(pagesToDelete);
		}
	};

	return (
		<div
			ref={containerRef}
			className={`page-thumbnail-container${isSelected ? ' sortable-selected' : ''}`}
			style={{
				width: `${containerWidth}px`,
				marginRight: '10px',
				height: '60px',
			}}
		>
			<div
				className="page-thumbnail-preview"
				style={{
					width: '100%',
					height: '100%',
					borderRadius: '15px',
					backgroundImage: preview ? `url("${preview}")` : 'none',
					backgroundRepeat: 'repeat-x',
					backgroundSize: 'auto 100%',
					backgroundColor: 'white',
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
				}}
				onClick={handleClick}
			>
				{!preview && <div className="page-thumbnail-loading" />}
			</div>
			<div
				className="page-thumbnail-border"
				style={{
					position: 'absolute',
					top: '0',
					left: '0px',
					bottom: '0px',
					right: '0px',
					borderRadius: '15px',
					border: isSelected
						? '2px solid rgb(0, 161, 255, 0.9)'
						: '2px solid lightgrey',
					zIndex: 1,
					pointerEvents: 'none',
				}}
			/>
			{!isListingOverlay && (
				<div className="page-thumbnail-menu" onClick={(e) => e.stopPropagation()}>
					<Popover
						content={
							<Menu style={{ width: '140px' }}>
								<MenuItem
									disabled={hasReachedLimit}
									icon={<Duplicate />}
									text={t ? t('pagesTimeline.duplicatePage', 'Duplicate Page') : 'Duplicate Page'}
									title={
										hasReachedLimit
											? (t ? t('pagesMax', `Maximum ${maxPages} pages`) : `Maximum ${maxPages} pages`)
											: undefined
									}
									onClick={handleDuplicate}
								/>
								<MenuItem
									disabled={hasReachedLimit}
									icon={<Insert />}
									text={t ? t('pagesTimeline.addPage', 'Add Page') : 'Add Page'}
									title={
										hasReachedLimit
											? (t ? t('pagesMax', `Maximum ${maxPages} pages`) : `Maximum ${maxPages} pages`)
											: undefined
									}
									onClick={handleInsert}
								/>
								{canDeletePage && (
									<MenuItem
										icon={<Trash />}
										text={t ? t('pagesTimeline.removePage', 'Remove Page') : 'Remove Page'}
										onClick={handleDelete}
									/>
								)}
							</Menu>
						}
						position={Position.TOP}
					>
						<Button
							icon={<More />}
							style={{ minHeight: '20px', borderRadius: '10px' }}
						/>
					</Popover>
				</div>
			)}
		</div>
	);
});

/**
 * Sortable pages container
 */
const SortablePages: React.FC<SortablePagesProps> = observer(({ store, maxPages, t, isListingOverlay = false }) => {
	const pagesList: PageListItem[] = store.pages.map((page: any) => ({ id: page.id }));

	const handleSort = (newList: PageListItem[]) => {
		newList.forEach(({ id }, newIndex) => {
			const page = store.pages.find((p: any) => p.id === id);
			const currentIndex = store.pages.indexOf(page);
			if (currentIndex !== newIndex) {
				page.setZIndex(newIndex);
			}
		});
	};

	return (
		<ReactSortable
			className="polotno-pages-container"
			delay={500}
			delayOnTouchOnly
			direction="horizontal"
			disabled={isListingOverlay}
			list={pagesList}
			setList={handleSort}
			style={{ display: 'flex', flexDirection: 'row' }}
		>
			{pagesList.map(({ id }) => {
				const page = store.pages.find((p: any) => p.id === id);
				return (
					<PageThumbnail
						key={id}
						isListingOverlay={isListingOverlay}
						maxPages={maxPages}
						page={page}
						t={t}
					/>
				);
			})}
		</ReactSortable>
	);
});

/**
 * Custom PagesTimeline component that respects max page limits from template configuration
 */
export const PagesTimeline: React.FC<PagesTimelineProps> = observer(({ store, t, globalState, isVideoEditingEnabled = false }) => {
	const templateState = globalState?.useTemplateState?.();
	const actingAsState = globalState?.useActingAsState?.();
	const projectState = globalState?.useProjectState ? globalState?.useProjectState() : null;
	const isListingOverlayTemplate = Boolean(
		templateState?.etc?.isListingOverlay ||
		templateState?.etc?.cfg?.order?.itemId === 'listing-overlays',
	);

	const [isOpen, setIsOpen] = useState(false);
	const hasAutoOpenedRef = useRef(false);

	// Determine max pages based on user role and configuration
	const maxPages =
		projectState?.cfg?.customer?.maxPages ||
		(actingAsState?.isGlobalRole && projectState?.cfg?.global?.maxPages) ||
		(actingAsState?.isGlobalRole && templateState?.etc?.cfg?.global?.maxPages) ||
		templateState?.etc?.cfg?.customer?.maxPages ||
		10;

	const pageCount = store?.pages?.length || 0;
	const hasReachedLimit = maxPages && pageCount >= maxPages;
	const timelineScrollRef = useRef<HTMLDivElement>(null);
	const elementsTimelineRef = useRef<HTMLDivElement>(null);
	const [timelineScale, setTimelineScale] = useState(0.02);
	const [timelineHeight, setTimelineHeight] = useState(TIMELINE_DEFAULT_HEIGHT);
	const resizeStateRef = useRef({
		isResizing: false,
		startY: 0,
		startHeight: TIMELINE_DEFAULT_HEIGHT,
	});
	const resizeMoveHandlerRef = useRef(null);
	const resizeUpHandlerRef = useRef(null);

	const showMediaTimeline = isVideoEditingEnabled && (storeHasVideoElements(store) || storeHasAudioElements(store));

	useEffect(() => {
		if (!showMediaTimeline) return;
		if (hasAutoOpenedRef.current) return;

		setIsOpen(true);
		hasAutoOpenedRef.current = true;
	}, [showMediaTimeline]);

	useEffect(() => {
		// Set an initial/base duration from current video content.
		(store?.pages || []).forEach((page: any) => {
			syncPageVideoDuration(page);
		});
	}, [store, store?.pages?.length]);

	// const showVideoTimeline = templateHasVideo;

	const handleAddPage = () => {
		if (isListingOverlayTemplate) return;
		if (hasReachedLimit) return;
		store.addPage({
			bleed: store.activePage?.bleed || 0,
		});
	};

	const addButtonTooltip = hasReachedLimit
		? t ? t('pagesMax', `Maximum ${maxPages} pages`) : `Maximum ${maxPages} pages`
		: t ? t('pagesTimeline.addPage', 'Add Page') : 'Add Page';
	const resizeTimelineLabel = t ? t('pagesTimeline.resizeTimeline', 'Resize timeline') : 'Resize timeline';

	const handleToggleTimeline = () => {
		setIsOpen((prev) => !prev);
	};

	const handleResizeMouseDown = (event) => {
		event.preventDefault();
		if (!showMediaTimeline) return;

		if (resizeMoveHandlerRef.current) {
			window.removeEventListener('mousemove', resizeMoveHandlerRef.current);
		}
		if (resizeUpHandlerRef.current) {
			window.removeEventListener('mouseup', resizeUpHandlerRef.current);
		}

		resizeStateRef.current.isResizing = true;
		resizeStateRef.current.startY = event.clientY;
		resizeStateRef.current.startHeight = timelineHeight;

		const onMouseMove = (moveEvent) => {
			if (!resizeStateRef.current.isResizing) return;

			const deltaY = moveEvent.clientY - resizeStateRef.current.startY;
			const nextHeight = Math.min(
				TIMELINE_MAX_HEIGHT,
				Math.max(TIMELINE_MIN_HEIGHT, resizeStateRef.current.startHeight - deltaY),
			);
			setTimelineHeight(nextHeight);
		};

		const onMouseUp = () => {
			window.removeEventListener('mousemove', onMouseMove);
			window.removeEventListener('mouseup', onMouseUp);
			resizeStateRef.current.isResizing = false;
			resizeMoveHandlerRef.current = null;
			resizeUpHandlerRef.current = null;
		};

		resizeMoveHandlerRef.current = onMouseMove;
		resizeUpHandlerRef.current = onMouseUp;
		window.addEventListener('mousemove', onMouseMove);
		window.addEventListener('mouseup', onMouseUp);
	};

	const handleResizeKeyDown = (event) => {
		if (!showMediaTimeline) return;

		if (event.key === 'ArrowUp') {
			event.preventDefault();
			setTimelineHeight((prevHeight) => Math.min(TIMELINE_MAX_HEIGHT, prevHeight + TIMELINE_RESIZE_STEP));
		} else if (event.key === 'ArrowDown') {
			event.preventDefault();
			setTimelineHeight((prevHeight) => Math.max(TIMELINE_MIN_HEIGHT, prevHeight - TIMELINE_RESIZE_STEP));
		}
	};

	useEffect(() => {
		return () => {
			if (resizeMoveHandlerRef.current) {
				window.removeEventListener('mousemove', resizeMoveHandlerRef.current);
			}
			if (resizeUpHandlerRef.current) {
				window.removeEventListener('mouseup', resizeUpHandlerRef.current);
			}
			resizeStateRef.current.isResizing = false;
		};
	}, []);

	const handleTimelineElementSelect = (event) => {
		if (event.button !== 0) {
			return;
		}

		// Ignore menu interactions so menu clicks don't change selected track.
		if (event.target.closest('.polotno-track-menu')) { // avoid selecting element when clicking on track menu
			return;
		}

		const timelineRoot = elementsTimelineRef.current;
		if (!timelineRoot) {
			return;
		}

		const elementsContainer = timelineRoot.querySelector('.polotno-elements-container');
		if (!elementsContainer) {
			return;
		}

		const rect = elementsContainer.getBoundingClientRect();
		const x = event.clientX - rect.left;
		const y = event.clientY - rect.top;

		if (x < 0 || y < 0 || x > rect.width || y > rect.height) {
			return;
		}

		// Translate click point into timeline coordinates:
		// x -> absolute time using current zoom scale,
		// y -> row index using Polotno row height constant.
		const time = x / timelineScale;
		const row = Math.floor(y / ELEMENT_ROW_HEIGHT);
		const tracks = getTimelineTracks(store);
		const hitTrack = tracks.find((track) => (
			track.row === row &&
			time >= track.start &&
			time <= track.end
		));

		if (!hitTrack) {
			return;
		}

		if (store.activePage?.id !== hitTrack.page.id) {
			store.selectPage(hitTrack.page.id);
		}

		store.selectElements([hitTrack.element.id]);
	};
	return (
		<>
			{/* Toggle button */}
			<div className="bay-pages-timeline-toggle">
				<div className="bay-pages-timeline-toggle-inner">
					<Navbar style={{ height: '35px', padding: '0 5px' }}>
						<Navbar.Group style={{ height: '35px' }}>
							<Button minimal onClick={handleToggleTimeline}>
								{t ? t('pagesTimeline.pages', 'Pages') : 'Pages'}
							</Button>
						</Navbar.Group>
					</Navbar>
				</div>
			</div>

			{/* Timeline */}
			{isOpen && (
				<Navbar
					className={`polotno-pages-timeline bay-pages-timeline bay-pages-timeline-panel${showMediaTimeline && isListingOverlayTemplate ? ' bay-pages-timeline--listing-overlay-video' : ''}`}
				>
					{showMediaTimeline && (
						<span
							className="position-absolute top-0 start-0 end-0 d-flex align-items-center justify-content-center translate-middle-y p-0 border-0 bg-transparent bay-pages-timeline-resize-handle"
							onMouseDown={handleResizeMouseDown}
							onKeyDown={handleResizeKeyDown}
							title="Drag to resize timeline"
							aria-label={resizeTimelineLabel}
						>
							<span aria-hidden="true" className="bg-primary rounded-pill opacity-50 w-100 h-25" />
						</span>
					)}
					{showMediaTimeline ? (
						<>
							<div className="d-flex justify-content-center pb-1">
								<PolotnoPlayButton
									scale={timelineScale}
									store={store}
									onScaleChange={setTimelineScale}
								/>
							</div>

							<div
								className="w-100 position-relative bay-pages-timeline-video-wrap"
								style={{ height: `${timelineHeight}px` }}
							>
								<div
									ref={timelineScrollRef}
									className="position-absolute top-0 start-0 end-0 bottom-0 overflow-auto-x bay-pages-timeline-video-scroll"
								>
									<div
										className="position-relative"
										style={{
											minWidth: `${(store?.duration || 0) * timelineScale}px`,
										}}
									>
										<PolotnoTimeRuler
											minWidth={(store?.duration || 0) * timelineScale}
											scale={timelineScale}
											store={store}
										/>

										<div className="position-relative d-flex">
											<div
												className="position-relative bay-pages-timeline-pages-height"
												style={{
													minWidth: `${(store?.duration || 0) * timelineScale}px`,
												}}
											>
												<PolotnoPages scale={timelineScale} store={store} />
											</div>

											{!isListingOverlayTemplate && (
												<Button
													className="bay-pages-timeline-add-button ms-3"
													disabled={hasReachedLimit}
													icon={<Plus />}
													minimal
													title={addButtonTooltip}
													onClick={handleAddPage}
												/>
											)}
										</div>
										<div
											ref={elementsTimelineRef}
											onMouseDownCapture={handleTimelineElementSelect}
										>
											<FilteredElementsTimeline
												store={store}
												scale={timelineScale}
												width={(store?.duration || 0) * timelineScale}
											/>
										</div>
										<PolotnoAudios
											scale={timelineScale}
											store={store}
											width={(store?.duration || 0) * timelineScale}
										/>

										<PolotnoCurrentTime
											dragAreaRef={timelineScrollRef}
											scale={timelineScale}
											store={store}
											variant="timeline"
										/>
									</div>
								</div>
							</div>
						</>
					) : (
						<div
							className="w-100 position-relative bay-pages-timeline-static-wrap"
							style={{ height: `${timelineHeight}px` }}
						>
							<div
								className="position-absolute top-0 start-0 end-0 bottom-0 d-flex overflow-auto-x bay-pages-timeline-static-scroll"
							>
								<div className="d-flex bay-pages-timeline-pages-height">
									<div className="position-relative">
										<SortablePages
											isListingOverlay={isListingOverlayTemplate}
											maxPages={maxPages}
											store={store}
											t={t}
										/>
									</div>
									{!isListingOverlayTemplate && (
										<Button
											className="bay-pages-timeline-add-button"
											disabled={hasReachedLimit}
											icon={<Plus />}
											minimal
											title={addButtonTooltip}
											onClick={handleAddPage}
										/>
									)}
								</div>
							</div>
						</div>
					)}
				</Navbar>
			)}
		</>
	);
});

export default PagesTimeline;
